import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface ABTestVariant {
  id: string;
  test_id: string;
  landing_page_id: string;
  name: string;
  traffic_percentage: number;
  views: number;
  conversions: number;
  is_control: boolean;
  landingPage?: {
    id: string;
    name: string;
    slug: string;
    published_url?: string;
    thumbnail?: string;
  };
}

export interface ABTestStats {
  variants: {
    id: string;
    name: string;
    isControl: boolean;
    trafficPercentage: number;
    views: number;
    conversions: number;
    conversionRate: number;
  }[];
  totalViews: number;
  totalConversions: number;
  overallConversionRate: number;
}

export interface ABTestSignificance {
  isSignificant: boolean;
  confidence: number;
  confidenceLevel: string | null;
  chiSquared: number;
  pValue?: number;
  winner?: string;
  sampleSize?: number;
  minRequired?: number;
  recommendation: string;
}

export interface ABTest {
  id: string;
  landing_page_id: string;
  user_id: string;
  name: string;
  slug: string;
  status: 'draft' | 'running' | 'paused' | 'completed';
  goal: 'conversions' | 'views' | 'time_on_page';
  started_at?: string;
  ended_at?: string;
  winner_variant_id?: string;
  confidence_level?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  variants?: ABTestVariant[];
  landingPage?: {
    id: string;
    name: string;
    slug: string;
    published_url?: string;
    thumbnail?: string;
  };
  stats?: ABTestStats;
  significance?: ABTestSignificance;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  pagination?: {
    total: number;
    page: number;
    totalPages: number;
    hasMore: boolean;
  };
}

interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    total: number;
    page: number;
    totalPages: number;
    hasMore: boolean;
  };
}

// Get all A/B tests for a specific landing page
export const getTestsForPage = async (
  landingPageId: string,
  params?: { page?: number; limit?: number; status?: string }
): Promise<PaginatedResponse<ABTest>> => {
  const response = await api.get(`/landing-pages/${landingPageId}/ab-tests`, { params });
  return response.data;
};

// Get all A/B tests for the user
export const getAllTests = async (
  params?: { page?: number; limit?: number; status?: string }
): Promise<PaginatedResponse<ABTest>> => {
  const response = await api.get('/landing-pages/ab-tests/all', { params });
  return response.data;
};

// Get single test with detailed stats
export const getTest = async (testId: string): Promise<ApiResponse<ABTest>> => {
  const response = await api.get(`/landing-pages/ab-tests/${testId}`);
  return response.data;
};

// Create new A/B test
export const createTest = async (
  landingPageId: string,
  data: {
    name?: string;
    slug?: string;
    goal?: 'conversions' | 'views' | 'time_on_page';
    variantPageId?: string;
    trafficSplit?: { control: number };
    createVariant?: boolean;
  }
): Promise<ApiResponse<ABTest>> => {
  const response = await api.post(`/landing-pages/${landingPageId}/ab-tests`, data);
  return response.data;
};

// Update test (start, pause, resume)
export const updateTest = async (
  testId: string,
  data: {
    status?: 'running' | 'paused';
    name?: string;
    notes?: string;
  }
): Promise<ApiResponse<ABTest>> => {
  const response = await api.put(`/landing-pages/ab-tests/${testId}`, data);
  return response.data;
};

// End test and determine winner
export const endTest = async (testId: string): Promise<ApiResponse<ABTest>> => {
  const response = await api.post(`/landing-pages/ab-tests/${testId}/end`);
  return response.data;
};

// Delete test
export const deleteTest = async (testId: string): Promise<ApiResponse<void>> => {
  const response = await api.delete(`/landing-pages/ab-tests/${testId}`);
  return response.data;
};

export default {
  getTestsForPage,
  getAllTests,
  getTest,
  createTest,
  updateTest,
  endTest,
  deleteTest
};
